import StripeEmbed from '../../../components/StripeEmbed';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import Modal from '../../../components/Modal';
import { BodyLarge, ParagraphLargeText } from '../../../components/Texts';
import { useSolana } from '../../../hooks/useSolana';
import Button from '../../../components/Button';
import { openCenteredWindow } from '../../../utils/window';
import { useStartDepositMutation } from '../../../redux/api/authorized/deposit';
import { useEffect, useState } from 'react';
import ReceiveCrypto from '../../../components/ReceiveCrypto';
import * as Sentry from '@sentry/react';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsEurope } from '../../../redux/slices/auth';
import { useFlag } from '@unleash/proxy-client-react';
import { openModal } from '../../../redux/slices/modals';
import {
  APPLE_PAY,
  BRIDGE,
  COINBASE,
  EVENT_DEPOSIT_OPTION_CLICKED,
  sendEvent,
  STRIPE,
  WEB3_WALLET,
} from '../../../common/analytics';
import LineAndLabel from '../../../components/LineAndLabel';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../../common/responsive';
import { Capacitor } from '@capacitor/core';
import useApplePay from '../../../hooks/useApplePay';
import ApplePayButton from '../../../components/ApplePayButton';

const Line = styled.div`
  height: 1px;
  background-color: ${({ theme }) => theme.border.dashed};
  width: 100%;
  margin-top: 15px;
  margin-bottom: 8px;
`;

const ContentContainer = styled.div`
  width: 100%;
  overflow: hidden;

  .stripe-onramp-container {
    min-height: 300px;
    iframe {
      height: 500px !important;
      min-height: unset !important;
      max-height: 500px !important;
      aspect-ratio: unset !important;
    }
  }
`;

const StyledModal = styled(Modal)`
  .modal-content {
    overflow: hidden;
    max-height: 700px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 16px;
`;

const getTitleKey = (mode) =>
  ({
    '': 'depositModal.title',
    transfer: 'depositModal.titleTransfer',
    stripe: 'depositModal.titleStripe',
    bridgeOnramp: 'depositModal.titleBridgeOnramp',
  }[mode || ''] || 'depositModal.title');

const AddFundsModal = ({
  onClose,
  amount,
  showBackButton = false,
  token = false,
}) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const { refreshTokenBalances } = useSolana();
  const [startDeposit] = useStartDepositMutation();
  const [coinbaseUrl, setCoinbaseUrl] = useState('');
  const [mode, setMode] = useState(token ? 'transfer' : null);
  const isEurope = useSelector(selectIsEurope);
  const dispatch = useDispatch();
  const { applePayEnabled, handleApplePay } = useApplePay();

  const bridgeOnrampEnabled = useFlag('bridgeOnrampEnabled');

  useEffect(() => {
    (async () => {
      try {
        const onrampUrl = await startDeposit({ network: 'solana' }).unwrap();
        setCoinbaseUrl(onrampUrl);
      } catch (err) {
        console.error('Error initializing Coinbase deposit:', err);
        Sentry.captureException(err, {
          tags: {
            component: 'AddFundsModal',
            action: 'startDeposit',
          },
        });
      }
    })();
  }, []);

  const handleSuccess = () => {
    refreshTokenBalances();
    onClose();
  };

  const handleDepositWithCoinbase = async () => {
    sendEvent(EVENT_DEPOSIT_OPTION_CLICKED, {
      provider: COINBASE,
    });
    openCenteredWindow(coinbaseUrl, {}, isMobile);
  };

  const handleDepositWithStripe = async () => {
    sendEvent(EVENT_DEPOSIT_OPTION_CLICKED, {
      provider: STRIPE,
    });
    setMode('stripe');
  };

  const handleTransferFromAnotherWallet = async () => {
    sendEvent(EVENT_DEPOSIT_OPTION_CLICKED, {
      provider: WEB3_WALLET,
    });
    setMode('transfer');
  };

  const handleDepositWithBridge = async () => {
    sendEvent(EVENT_DEPOSIT_OPTION_CLICKED, {
      provider: BRIDGE,
    });
    dispatch(
      openModal({
        name: 'bridgeOnramp',
      }),
    );
  };

  const theme = useTheme();

  return (
    <StyledModal
      onClose={onClose}
      breakpoints={[0, 0.5, 1]}
      initialBreakpoint={1}
      onBack={
        showBackButton
          ? () => {
              if (!mode) {
                dispatch(
                  openModal({
                    name: 'withdrawOrDeposit',
                  }),
                );
              } else {
                setMode(null);
              }
            }
          : undefined
      }
    >
      <ContentContainer>
        <ParagraphLargeText
          style={{ textAlign: 'center', color: theme.text.title }}
        >
          {t(getTitleKey(mode))}
        </ParagraphLargeText>
        {!mode && (
          <ParagraphLargeText
            style={{
              textAlign: 'center',
              color: theme.text.secondary,
              marginTop: 8,
              fontSize: 14,
            }}
          >
            {t('depositModal.subtitle')}
          </ParagraphLargeText>
        )}
        {mode === 'transfer' && (
          <ReceiveCrypto onBack={token ? undefined : () => setMode(null)} />
        )}
        {mode === 'stripe' && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <StripeEmbed amount={amount} onSuccess={handleSuccess} />
            <Button
              variant='secondary'
              fullWidth={true}
              onClick={() => setMode(null)}
            >
              {t('common.back')}
            </Button>
          </div>
        )}
        {!mode && (
          <ButtonContainer>
            {applePayEnabled && (
              <ApplePayButton
                onClick={() => {
                  sendEvent(EVENT_DEPOSIT_OPTION_CLICKED, {
                    provider: APPLE_PAY,
                  });
                  handleApplePay();
                }}
              />
            )}
            {bridgeOnrampEnabled && (
              <Button
                variant={Capacitor.isNativePlatform() ? 'primary' : 'secondary'}
                onClick={handleDepositWithBridge}
                fullWidth={true}
              >
                {t('depositModal.bankTransfer')}
              </Button>
            )}
            {/* {!Capacitor.isNativePlatform() && (
              <>
                <LineAndLabel text={t('depositModal.debitCreditCard')} />
                <Button
                  disabled={!coinbaseUrl}
                  onClick={handleDepositWithCoinbase}
                  fullWidth={true}
                  style={{
                    backgroundColor: `${theme.primary.accent}`,
                  }}
                >
                  {t('depositModal.viaCoinbase')}
                </Button>
                {!isEurope && (
                  <Button
                    onClick={handleDepositWithStripe}
                    fullWidth={true}
                    style={{
                      backgroundColor: `${theme.primary.purple}`,
                    }}
                  >
                    {t('depositModal.viaStripe')}
                  </Button>
                )}
                <Line />
              </>
            )} */}
            <Button
              onClick={handleTransferFromAnotherWallet}
              variant='secondary'
              fullWidth={true}
            >
              {t('depositModal.externalWallet')}
            </Button>
          </ButtonContainer>
        )}
      </ContentContainer>
    </StyledModal>
  );
};

export default AddFundsModal;
